import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { capabilityChain, developmentCycle } from '../../data/semanticKnowledge';
import { fadeUp } from '../../utils/motion';
export function SkpCapabilityFoundation() {
  return <section className="bg-white py-20 lg:py-28" data-id="element-2368" data-mp="1j6iziegpt4zq">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2369" data-mp="1e8r6fh4c3cty">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-2370" data-mp="1s9k1q42gmby4">
          <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-2371" data-mp="1s7h3wxv2feiu">
            <h2 className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight mb-6" data-id="element-2372" data-mp="1n9pecahw84ht">
              The knowledge foundation for CJI and NCJI
            </h2>
            <p className="text-xl text-ink-dark leading-relaxed mb-6" data-id="element-2373" data-mp="1x1bnm1ao7thx">
              The Human Capability Framework uses SKP to maintain the
              definitions and evidence behind the{' '}
              <a href="/cji" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-2374" data-mp="26zhnkdcx2pk5">
                Civic Judgment Index (CJI)
              </a>{' '}
              and{' '}
              <a href="/ncji" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-2375" data-mp="1df87c8r31xmz">
                Neurocognitive Civic Judgment Index (NCJI)
              </a>
              .
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2376" data-mp="1sbz2u6xmyp7b">
              SKP connects each capability domain to its indicators, assessment
              tasks, interpretation rules, learning activities, and later
              outcomes. It records which versions were used so that an
              assessment can be explained and, when necessary, reconsidered.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7" data-id="element-2377" data-mp="2b2qdrcx9unnt">
            <div className="bg-surface-muted rounded-lg p-8" data-id="element-2378" data-mp="2b0dpki0x7h2b">
              <div className="flex items-center justify-between mb-6" data-id="element-2379" data-mp="2g2mjphpj4p60">
                <p className="text-sm font-medium text-ink-dark" data-id="element-2380" data-mp="1qd5zmi64fx02">Connected in SKP</p>
                <span className="text-xs font-medium text-navy bg-white border border-line rounded px-2 py-1" data-id="element-2381" data-mp="1lecp8ym4sm44">
                  Version recorded
                </span>
              </div>
              <ol className="space-y-2" data-id="element-2382" data-mp="20522s7n2edb5">
                {capabilityChain.map((item, index) => <li key={item} className="flex items-center gap-4 bg-white border border-line rounded-md px-4 py-3" data-id="element-2383" data-mp="1vao0dl9zj5wz" data-mpi={index}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${index === 0 ? 'bg-navy' : 'bg-teal'}`} data-id="element-2384" data-mp="244knp3evyodz" />
                    <span className={`text-ink-dark ${index === 0 ? 'font-heading font-bold' : 'font-medium'}`} data-id="element-2385" data-mp="2e2gs7cdbil34">
                      {item}
                    </span>
                  </li>)}
              </ol>
            </div>
          </motion.div>
        </div>

        <motion.div {...fadeUp} className="mt-20 pt-14 border-t border-line" data-id="element-2386" data-mp="2vdnimszik5yt">
          <p className="text-lg text-ink-muted leading-relaxed max-w-3xl mb-10" data-id="element-2387" data-mp="30bozd79gvt74">
            This supports the NCJI’s developmental purpose: identifying an area
            for growth, explaining the finding, recommending a pathway, and
            measuring progress over time.
          </p>
          <ol className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0" data-id="element-2388" data-mp="2clbh28gop8pq">
            {developmentCycle.map((step, index) => <li key={step.title} className="flex items-start md:pr-6" data-id="element-2389" data-mp="2go7t1gbpy6kp" data-mpi={index}>
                <div className="flex-1" data-id="element-2390" data-mp="22ogl9xbspmf5">
                  <p className="font-heading font-bold text-2xl text-navy mb-1" data-id="element-2391" data-mp="22phudw993yu8">
                    {step.title}
                  </p>
                  <p className="text-ink-muted" data-id="element-2392" data-mp="wjytso3as5l">{step.body}</p>
                </div>
                {index < developmentCycle.length - 1 && <ArrowRight size={20} aria-hidden="true" className="hidden md:block text-line mt-1.5 shrink-0" data-id="element-2393" data-mp="5u2cfwd1xvjx" />}
              </li>)}
          </ol>
          <p className="mt-10 text-lg text-ink-muted leading-relaxed max-w-3xl" data-id="element-2394" data-mp="1sxlcgd125rr6">
            Opportunity and role models can be connected to demonstrated
            capabilities while keeping role requirements separate from a
            person’s assessment record.
          </p>
          <p className="mt-10 font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug max-w-4xl border-l-4 border-teal pl-6" data-id="element-2395" data-mp="1j1icndtoi57e">
            A recommendation is a starting point for review and development,
            not an irreversible verdict about a person.
          </p>
        </motion.div>
      </div>
    </section>;
}